/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import SwipeableViews from 'react-swipeable-views';
import { MapPinIcon } from '@heroicons/react/20/solid';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';
import {
  DocumentTextIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { Mapbox } from '@views/WarehouseDetails/Mapbox';
import { DEPOT_GET, DEPOT_IMAGES_URL } from '../../constants';
import { Button } from '../../tailwindComponents/Button';
import { useAuthHttp } from '../../hooks';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Subheader } from '../../tailwindComponents/Subheader';
import { useDocumentTitle } from 'usehooks-ts';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';

export type Depot = {
  depotId: string;
  name: string;
  description?: string;
  address: string;
  postcode?: string;
  city?: string;
  country?: string;
  location: { lat: number; lon: number };
  contactName?: string;
  contactEmail?: string;
  phoneNumber?: {
    countryCode: string;
    countryNumberPrefix: string;
    number: string;
  };
  loadingDocks?: number;
  standardBusinessHoursType?: string;
  hasFlexibleBusinessHours?: boolean;
  customBusinessHours?: Array<{ day: string; open?: string; close?: string }>;
  vehicleLoadingTimeWindows?: Array<{
    day: string;
    open?: string;
    close?: string;
  }>;
  fleet?: Array<{ type: string; count: number }>;
  images?: Array<{
    id: string;
    title: string;
    originalFileName: string;
    remoteName: string;
    imageLocation: string;
  }>;
  isDelisted?: boolean;
  isDeleted?: boolean;
  isDraft?: boolean;
  createdAt?: string;
  modifiedAt?: string;
  userBusinessId?: string;
  userBusinessName?: string;
};

const FLEET_TYPE_LABELS: Record<string, string> = {
  MultiAxleArticMoreThan26t: 'Artics over 26t',
  ThreeAxleRigidLessThan26t: 'Rigids under 26t',
  TwoAxleRigid18t: 'Rigids under 18t',
  TwoAxleRigidLessThan7AndHalf5t: 'Rigids under 7.5t',
  LgvLessThan3AndHalft: 'LDVs under 3.5t',
  VanLessThan1AndHalft: 'Vans under 1.5t',
  SpecialOrOther: 'Special / Other',
};

function DataField({
  name,
  value,
}: {
  name: string;
  value?: string | number | React.ReactNode;
}) {
  return (
    <div className='flex flex-col items-start font-inter h-full justify-center'>
      <span className='text-gray-900 text-base font-medium mb-1'>
        {value ?? 'N/A'}
      </span>
      <span className='text-gray-400 text-sm'>{name}</span>
    </div>
  );
}

function DayHours({ day, hours }: { day: string; hours: string }) {
  const isClosed = hours === '00:00 - 00:00';
  return (
    <div className='flex items-center font-inter w-full min-w-[320px]'>
      <span className='text-sm font-normal text-gray-500'>{day}</span>
      <span className='flex-grow border-t border-dotted border-gray-300 mx-4' />
      <span className='text-sm font-normal text-gray-900'>
        {isClosed ? 'closed' : hours}
      </span>
    </div>
  );
}

const depotImagePlaceholder = `/images/warehouse_details_placeholder.png`;

export function DepotDetails() {
  useDocumentTitle('FLOX - Depot Details');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const [depot, setDepot] = useState<Depot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  const fetchDepot = async (isStillOnThisDepot: () => boolean) => {
    setIsLoading(true);
    try {
      const { data }: AxiosResponse<{ depot: Depot }> = await authHttp.get(
        DEPOT_GET,
        { params: { depotId: id } }
      );
      if (isStillOnThisDepot()) setDepot(data.depot);
    } catch (error) {
      console.error(error);
    } finally {
      if (isStillOnThisDepot()) setIsLoading(false);
    }
  };

  useEffect(() => {
    let isStillOnThisDepot = true;
    setDepot(null);
    setActiveStep(0);
    fetchDepot(() => isStillOnThisDepot);
    return () => {
      isStillOnThisDepot = false;
    };
  }, [id]);

  if (isLoading) {
    return (
      <div className='flex justify-center items-center mt-48'>
        <FloxSpinner size='lg' />
      </div>
    );
  }

  if (!depot) {
    return (
      <div className='flex justify-center items-center mt-48'>
        <p className='text-gray-700'>No depot found</p>
      </div>
    );
  }

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Depots', href: '/depot-master', current: false },
    { name: depot.name, href: `/depot/${depot.depotId}`, current: true },
  ];

  const fleetEntries =
    depot.fleet?.filter((v) => v.count && Number(v.count) > 0) || [];

  const images = (depot.images || []).map((image) => ({
    imgPath: `${DEPOT_IMAGES_URL}/${image.imageLocation}/small/${image.remoteName}`,
  }));
  const maxSteps = images.length;

  return (
    <>
      <PageHeader
        header='Depot'
        pages={pages}
        subHeader={
          <span className='flex items-center gap-1'>
            <MapPinIcon className='w-6' color='red' />
            {depot.city ? depot.city : depot.address}
          </span>
        }
      >
        <Button
          label=''
          startIcon={
            <PencilIcon
              aria-hidden='true'
              className='h-5 w-5'
              color='black'
            />
          }
          intent='secondary'
          size='iconOnly'
          onClick={() => navigate(`/depot/update/${depot.depotId}`)}
        />
      </PageHeader>

      {/* Draft Banner */}
      {depot.isDraft && (
        <div className='mx-10 mt-6 p-4 bg-gray-50 border border-gray-300 rounded-xl flex items-start gap-3'>
          <div className='w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0'>
            <DocumentTextIcon className='w-5 h-5 text-gray-500' />
          </div>
          <div>
            <p className='text-sm font-semibold text-gray-800'>
              This depot is a draft
            </p>
            <p className='text-sm text-gray-600 mt-1'>
              This depot has not been submitted yet and is not visible to
              customers. Complete all required fields and submit to make it live.
            </p>
          </div>
        </div>
      )}

      <div className='flex flex-col gap-8 px-10 py-6 grow font-inter'>
        {/* Images */}
        <div className='flex justify-between items-center gap-8'>
          <div className='flex flex-col gap-3 w-full max-w-[400px]'>
            {images.length === 0 ? (
              <img
                className='block h-[255px] max-w-[400px] min-w-[200px] w-full overflow-hidden rounded-xl'
                src={depotImagePlaceholder}
                alt='Depot'
              />
            ) : images.length > 1 ? (
              <div className='relative'>
                <div className='overflow-hidden'>
                  <SwipeableViews
                    enableMouseEvents
                    index={activeStep}
                    onChangeIndex={handleStepChange}
                  >
                    {images.map((step, index) => (
                      <img
                        key={index}
                        className={`block h-[255px] max-w-[400px] min-w-[200px] w-full overflow-hidden rounded-xl ${
                          index === activeStep ? '' : 'hidden'
                        }`}
                        src={step.imgPath}
                        alt={`Depot ${index + 1}`}
                      />
                    ))}
                  </SwipeableViews>
                </div>
                <div className='flex justify-between items-center bg-gray-100 px-2 py-1 mt-2'>
                  <button
                    className='text-gray-700 px-2 py-1 disabled:opacity-50'
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    type='button'
                  >
                    <ChevronLeftIcon className='h-6 w-6' />
                  </button>
                  <span className='text-sm text-gray-500'>
                    {activeStep + 1} / {maxSteps}
                  </span>
                  <button
                    className='text-gray-700 px-2 py-1 disabled:opacity-50'
                    onClick={handleNext}
                    disabled={activeStep === maxSteps - 1}
                    type='button'
                  >
                    <ChevronRightIcon className='h-6 w-6' />
                  </button>
                </div>
              </div>
            ) : (
              <img
                className='block h-[255px] max-w-[400px] min-w-[200px] w-full overflow-hidden rounded-xl'
                src={images[0].imgPath}
                alt='Depot'
              />
            )}
          </div>
          <Mapbox warehouse={depot} interactive={false} />
        </div>

        {/* Last updated */}
        {depot.modifiedAt && (
          <div className='text-gray-500 text-sm mt-2'>
            Last updated: {moment(depot.modifiedAt).format('DD/MM/YYYY')}
          </div>
        )}

        {/* Description */}
        {depot.description && <div>{depot.description}</div>}

        {/* Basic Info */}
        <div className='grid grid-cols-4 gap-x-4 gap-y-4'>
          <DataField
            name='Company Name'
            value={depot.userBusinessName}
          />
          <DataField name='Contact Name' value={depot.contactName} />
          <DataField name='Contact Email' value={depot.contactEmail} />
          <DataField
            name='Phone'
            value={
              depot.phoneNumber?.number
                ? `+${depot.phoneNumber.countryNumberPrefix || ''} ${depot.phoneNumber.number.replace(/^0+/, '')}`
                : undefined
            }
          />
          <DataField name='Loading Docks' value={depot.loadingDocks} />
        </div>

        {/* Hours */}
        <Subheader subHeader='Hours' />
        <div className='flex flex-col gap-4'>
          <div className='flex gap-32'>
            <div className='flex flex-col gap-4'>
              <div className='font-semibold text-gray-900 text-base mb-2'>
                Working hours
              </div>
              {!depot.customBusinessHours ||
              depot.customBusinessHours.length === 0 ? (
                depot.standardBusinessHoursType === 'Standard24/7' ? (
                  <>
                    <DayHours day='Monday' hours='00:00 - 23:59' />
                    <DayHours day='Tuesday' hours='00:00 - 23:59' />
                    <DayHours day='Wednesday' hours='00:00 - 23:59' />
                    <DayHours day='Thursday' hours='00:00 - 23:59' />
                    <DayHours day='Friday' hours='00:00 - 23:59' />
                    <DayHours day='Saturday' hours='closed' />
                    <DayHours day='Sunday' hours='closed' />
                  </>
                ) : (
                  'N/A'
                )
              ) : (
                depot.customBusinessHours.map((window) => (
                  <DayHours
                    key={window.day}
                    day={window.day}
                    hours={
                      window.open && window.close
                        ? `${window.open.slice(0, 5)} - ${window.close.slice(0, 5)}`
                        : 'closed'
                    }
                  />
                ))
              )}
            </div>
            {depot.vehicleLoadingTimeWindows &&
              depot.vehicleLoadingTimeWindows.length > 0 && (
                <div className='flex flex-col gap-4'>
                  <div className='font-semibold text-gray-900 text-base mb-2'>
                    Loading hours
                  </div>
                  {depot.vehicleLoadingTimeWindows.map((window) => (
                    <DayHours
                      key={window.day}
                      day={window.day}
                      hours={
                        window.open && window.close
                          ? `${window.open.slice(0, 5)} - ${window.close.slice(0, 5)}`
                          : 'closed'
                      }
                    />
                  ))}
                </div>
              )}
          </div>
          <DataField
            name='Flexible business hours'
            value={depot.hasFlexibleBusinessHours ? 'Yes' : 'No'}
          />
        </div>

        {/* Fleet */}
        {fleetEntries.length > 0 && (
          <>
            <Subheader subHeader='Fleet' />
            <div className='grid grid-cols-4 gap-x-4 gap-y-4'>
              {fleetEntries.map((fleetItem) => (
                <DataField
                  key={fleetItem.type}
                  name={
                    FLEET_TYPE_LABELS[fleetItem.type] || fleetItem.type
                  }
                  value={fleetItem.count}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
